import { Injectable } from "@angular/core";
import { Account, WorkflowService } from "@mitel/cloudlink-sdk";
import { AdminService } from '../../core/services/admin.service';
import { AppInjector } from "../../app-injector";
import { IntegrationItemServiceInf, StatusString } from "../interfaces/integrations";
import { MenuItem } from "../interfaces/menu-item";
import { UserRoles } from "../constants/constants";
import { ConfirmationDialogComponent } from "../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmationDialogs } from "../../shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum";
import { RBACAuthGuardService } from '../guards/rbac-auth-guard.service';
import { IntegSharedCCService } from "./integ-shared-cc.service";
import { Util } from "../utils/Util";
import { TranslateService } from '@ngx-translate/core';
import { SpinnerService } from "./spinner.service";
import { SystemManagerService } from "./system-manager.service";

@Injectable({
	providedIn: 'root'
})
export class IntegVoiceAssistService implements IntegrationItemServiceInf {

    private sharedCCService: IntegSharedCCService = undefined;

    private workflowService: WorkflowService;
    private adminService: AdminService;
    private dialog: MatDialog;
    private rbacAuthGuardService: RBACAuthGuardService;
    private translateService: TranslateService = undefined;
    private spinnerService: SpinnerService;
    private systemManagerService: SystemManagerService;

    private EndpointRingingGUID = 'global_e5a006c1-bca4-46e6-8143-69ce8510127b';
    private VoiceAssistTag = 'VOICE_ASSIST';

    constructor() { }

    initServices(): void {
        if(!this.adminService){
            this.adminService = AppInjector.injector.get(AdminService);
        }
        if (!this.dialog){
            this.dialog = AppInjector.injector.get(MatDialog);
        }
        if(!this.workflowService) {
            this.workflowService = AppInjector.injector.get(WorkflowService);
        }
        if (!this.rbacAuthGuardService)
            this.rbacAuthGuardService = AppInjector.injector.get(RBACAuthGuardService);

        if (!this.sharedCCService)
            this.sharedCCService = AppInjector.injector.get(IntegSharedCCService);

        if (!this.translateService)
            this.translateService = AppInjector.injector.get(TranslateService);

        if (!this.spinnerService)
            this.spinnerService = AppInjector.injector.get(SpinnerService);

        if (!this.systemManagerService)
            this.systemManagerService = AppInjector.injector.get(SystemManagerService);
    }


    // VA is restricted from being added since Workflow is now available
	canBeSelected(account: Account): Promise<boolean>{
        return Promise.resolve(false);
	}

    getConfigurationDialog(setupComplete?: boolean) {
        this.initServices();
        return this.sharedCCService.getConfigurationDialog();
    }

    isSetupComplete(account: Account): Promise<boolean> {
        this.initServices();
        return  this.adminService.getAccountTags(account.accountId).then( tags => {
            console.log("VoiceAssist isSetupComplete, tags", tags)
            if ( tags && tags.voiceAssist && tags.voiceAssist.state == 'FINISHED' )
                return true;
            else
                return false;
        })
        .catch( err => {
            console.error("Fetching tags fails, so there is no setup complete", err)
            return false;
        });
    }

    canSetupProceed(account: Account): Promise<boolean> {
        this.initServices();

        // Verify there is an onboarded PBX integration
        return this.adminService.getAccountTags(account.accountId).then(tags => {
            if (Util.doesAccountHaveVoiceAssistPBXOnboarded(tags) == true) {
                return true;
            }

            // Newer systems have a platform added that is retrieved through system-manager
            // Supported platforms for media:  "os4k"=OS 4K, "msl"=MiVB/MX-ONE, "mivck"=MiVoice 5000, "osbizs"=OpenScape Business
            return this.systemManagerService.getPlatforms().then(platforms => {
                const SupportedPlatforms = ['os4k', 'msl', 'mivck', 'osbizs'];
                if (platforms && platforms.find(item => SupportedPlatforms.includes(item.platformType))) {
                    return true;
                }

                // show the dialog that says what the requirements are to have Voice Assist integration
                const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.MVA_SETUP_FAILURE } });
                return dialogRef.afterClosed().toPromise().then(result => {
                    return false;
                });

            }).catch(error => {
                console.error("Error fetching platforms", error);
                return false;
            });
        })
    }

    async handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[]): Promise<any> {
        if(!state) {
            this.initServices();
            const tags = await this.adminService.getAccountTags(account.accountId);
            if (tags?.voiceAssist) {

                // Teardown will be performed via iFrame to cc-admin
                return this.sharedCCService.handleIntegrationStateChange(account, state, 'VoiceAssist');
            }
            return { }
        }
    }

    getStatusString(account: Account): Promise<StatusString> {
        return undefined;
    }

    getConfigButtonLabel(account: Account): Promise<string> {
        return undefined;
    }

    isButtonDisabled(account: Account): Promise<boolean> {
        return Promise.resolve(false);
    }

    canDeleteIntegration(account: Account): Promise<boolean> {
        this.initServices();
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.REMOVE_VOICEASSIST_CONFIRMATION } });
        return dialogRef.afterClosed().toPromise().then(result => {
            return result;
        });
    }

    async deleteAppGroupsAndFlowsForVoiceAssist(accountId: string): Promise<void> {

        this.spinnerService.setSpinnerMessageByKey('spinnerMessages.deletingGroup');
        this.spinnerService.showSpinner();

        // Find all the Voice Assist tagged flows
        const flowList = await this.workflowService.getActivitiesByTag({ tagId: this.VoiceAssistTag });
        const wfItems = flowList?._embedded?.items || [];

        await Promise.all(wfItems.map(async workflow => {

            // Check for any Endpoint Ringing triggers in the WF
            if (workflow.trigger.$type === 'CloudlinkTrigger' && workflow.trigger.template === this.EndpointRingingGUID) {

                // Extract the number(s) assigned to the Endpoint Ringing trigger
                const numbers: string[] = [];
                const expression: string = workflow.trigger.throughputs?.Endpoint?.expression || '';
                const assignedNumbers = expression.split('|');

                assignedNumbers.forEach(num => {
                    num = num.replace('\\', '').trim(); // treat regex escape
                    if (num.length > 0) {
                        numbers.push(num);
                    }
                });

                // Retrieve the app groups associated with each number to get the group Id
                const appGroups = await Promise.all(numbers.map(async number => {
                    return this.adminService.getAppGroups(accountId, `dialableNumber eq ${number} and type eq 'APP'`);
                }));

                // Delete the app groups associated with the numbers
                try {
                    await Promise.all(appGroups.map(groupList => {
                        groupList?._embedded?.items?.map(item => {
                            this.adminService.deleteAppGroup(accountId, item.groupId);
                        });
                    }));

                    // If all the app groups are deleted, delete the WF
                    await this.workflowService.deleteActivityById({ activityId: workflow.id });
                } catch (error) {
                    console.log(`Error deleting app groups for WF ${workflow.id} - ` + error);
                }
            }
        }));

        this.spinnerService.hideSpinner();
    }

    getLeftNavMenus(account: Account): Promise<MenuItem[]> {
        this.initServices();
        return this.isSetupComplete(account).then((complete) => {
          if (complete == false)
            // setup has to be complete before we can display the menus
            return [];

          const dashboardItem = {
            name: "ccDashboard",
            translationKey: "About",
            id: "ccdashboard",
            minRole: UserRoles.ACCOUNT_ADMIN,
            navigation: `/accounts/${account.accountId}/ccdashboard`,
          };

          const routingItem = {
            name: "varouting",
            translationKey: "Routing",
            id: "varoutings",
            minRole: UserRoles.ACCOUNT_ADMIN,
            navigation: `/accounts/${account.accountId}/varouting`,
          };

          const integrationsItem = {
            name: "ccIntegrations",
            translationKey: "Integrations",
            id: "ccintegrations",
            minRole: UserRoles.ACCOUNT_ADMIN,
            navigation: `/accounts/${account.accountId}/ccintegrations`,
          };

          const numbersItem = {
            name: "ccNumbers",
            translationKey: "Numbers",
            id: "ccnumberss",
            minRole: UserRoles.ACCOUNT_ADMIN,
            navigation: `/accounts/${account.accountId}/ccnumbers`,
          };

          const helpItem = {
            name: "ccHelp",
            translationKey: "Help",
            id: "cchelpvoiceassist",
            minRole: UserRoles.ACCOUNT_ADMIN,
            navigation: this.translateService.instant(
              "integrations.voice-assist.help-url"
            ),
            openInNewWindow: true,
          };

          const voiceAssistItem = {
            name: "voiceAssist",
            translationKey: "VoiceAssist",
            id: "voiceAssist",
            minRole: UserRoles.ACCOUNT_ADMIN,
            navigation: "/norouting",
            iconActive: "../../../../assets/images/mitel-voice-assist-icon-selected.svg",
            iconInactive: "../../../../assets/images/mitel-voice-assist-icon.svg",
            children: [
              dashboardItem,
              routingItem,
              integrationsItem,
              numbersItem,
              helpItem
            ],
          };
          console.log("return left nav menus", [voiceAssistItem]);
          return Promise.resolve([voiceAssistItem]);
        });
    }

}
